/// <amd-module name="VC/ProjectOverview/CodeAboutRegion" />
import * as React from "react";

import { getId } from "VSS/Core/Util/Object";
import { format } from "VSS/Core/Util/String";
import { Spinner } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

import * as Promise_Util from "VSS/Core/Util/Promise";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";

import { RepositoryDropdown } from "Repos/RepoDropdown/Components/RepositoryDropdown";
import { IRepositoryDropdownItem, IRepositoryDropdownOptions } from "Repos/RepoDropdown/RepositoryDropdownProvider";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IListBoxItem } from "VSSUI/ListBox";

import { GitItem, GitRepository, TfvcItem } from "VC/Common/Contracts/Git";
import { IGitRestClient } from "VC/Common/RestClient/Git";
import { ITfvcRestClient } from "VC/Common/RestClient/Tfvc";
import { getFriendlyName, getVersionFromBranchName } from "VC/Common/Utils/Ref";
import * as Resources from "VC/ProjectOverview/Resources";

interface IRendererOptions {
    repository?: GitRepository;
    branchName?: string;
    filePath: string;
    content: string;
}

interface CodeRepositoryDataProviderData {
    codeRepositoryData: {
        repository: GitRepository;
    };
}

interface CodeReadmeDataProviderData {
    codeReadmeData: {
        repository: GitRepository;
        content: string;
        isTfvc: boolean;
    };
    supportsTfvc: boolean;
    hasCodeAccess: boolean;
}

const enum FetchingState {
    FetchingInitial,
    FetchingReadme,
    NotFetching,
    FetchingFailed
}

interface IErrorOptions {
    title: string;
    message: string;
}

interface errorStatus {
    [status: string]: number;
}

interface ICodeAboutRegionState {
    fetchingState: FetchingState;
    showReadmeLink?: boolean;
    dropdownOptions?: IRepositoryDropdownOptions;
    repositoryDropdownItem?: IRepositoryDropdownItem;
    rendererOptions?: IRendererOptions;
    errorOptions?: IErrorOptions;
}

interface ICodeAboutRegionProps extends IVssComponentProperties {
    readMode: boolean;
    repoId?: string;
    onRepositoryChanged(repoId: string): void;
    markTimeToInteractive(source: string): void;
    showError: boolean;
}

export class CodeAboutRegion extends VssComponent<ICodeAboutRegionProps, ICodeAboutRegionState> {
    private static PROVIDER_ID = "ms.vss-code-web.code-readme-data-provider-verticals";
    private readonly _readmeFileName = "/README.md";
    private readonly _readmeString = "README.md";
    private readonly _codeSource = "Code";
    private _gitClient: IGitRestClient;
    private _ttiMarked = false;
    private readonly _tfvcPrefix = "$/";
    private readonly _repoDropdownAriaDescribedById = getId("repo-picker-description");

    private readonly _abortSplitTimeName = "Code.FetchInitialData.Abort";
    private readonly _endSplitTimeName = "Code.FetchInitialData.End";
    private _logSplitTimeInRender = true;

    constructor(props: ICodeAboutRegionProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { fetchingState: FetchingState.FetchingInitial };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.props.readMode) {
            this._addSplitTiming("Code.GetReadmeData.Start");

            this.trackPromise(this.context.pageContext.getService<IVssContributionService>("IVssContributionService")
                .getDataAsync<CodeReadmeDataProviderData>(CodeAboutRegion.PROVIDER_ID))
                .promise
                .then(data => {
                    this._addSplitTiming("Code.GetReadmeData.End");
                    if (data) {
                        this.setState({ showReadmeLink: data.hasCodeAccess });
                    }
                    // readme can be from either tfvc or git, check if one of them is true. Also repoId can't be undefined.
                    if (data && data.codeReadmeData && (data.codeReadmeData.isTfvc || data.codeReadmeData.repository) && this.props.repoId) {
                        const { content, repository, isTfvc } = data.codeReadmeData;
                        const { repoId } = this.props;
                        // use the data provider only if repoIds are same, as data provider might not contain the latest data in cases
                        // like repo is changed by chosing a different repo via edit panel.
                        if ((repoId.startsWith(this._tfvcPrefix) && isTfvc) || (repository && repoId === repository.id)) {
                            const { projectId, projectName } = this._getProjectInfo(this.context.pageContext);
                            if (content != null) {
                                this.setState({
                                    fetchingState: FetchingState.NotFetching,
                                    rendererOptions: {
                                        repository,
                                        content,
                                        branchName: repository ? repository.defaultBranch : undefined,
                                        filePath: repoId.startsWith(this._tfvcPrefix) ? repoId + this._readmeFileName : this._readmeFileName
                                    },
                                    dropdownOptions: { projectName },
                                    showReadmeLink: data.hasCodeAccess
                                });
                            } else {
                                // if content is not available, show appropriate error message
                                const supportsTfvc = data && data.supportsTfvc;
                                const dropdownOptions = this.getDefaultDropdownOptions(projectId, projectName, supportsTfvc);
                                this._onFetchingInitialDataFailed(dropdownOptions);
                            }
                        } else { // this happens when the readme setting is changed as follows: repoId1 --> Wiki ---> repoId2
                            this._fetchInitialData(this.props, this.context);
                        }
                    } else {
                        this._fetchInitialData(this.props, this.context);
                    }
                }, (error: Promise_Util.ICancelReason) => {
                    if (!error.isCanceled) {
                        this._addSplitTiming("Code.GetReadmeData.Failed");
                        this._fetchInitialData(this.props, this.context);
                    }
                })
        } else {
            this._fetchInitialData(this.props, this.context);
        }
    }

    public componentDidUpdate(prevProps: ICodeAboutRegionProps, prevState: ICodeAboutRegionState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (prevProps.repoId !== this.props.repoId) {
            this._fetchInitialData(this.props, this.context);
        }

        if (
            !this._ttiMarked &&
            this.props.readMode &&
            (this.state.fetchingState === FetchingState.FetchingFailed)
        ) {
            // incase of error, content will not get rendered. So we will mark tti here.
            this.props.markTimeToInteractive(this._codeSource);
            this._ttiMarked = true;
        }
    }

    public render(): JSX.Element {
        if (this.state.fetchingState === FetchingState.FetchingInitial) {
            // load the legacy component files, while fetching the actual data.
            if (this._logSplitTimeInRender) {
                this._addSplitTiming("Code.FetchLegacyComponent.Start");
                this._logSplitTimeInRender = false;
            }
            return (
                <>
                    {this._loadingComponent()}
                    <LegacyComponent
                        wrappedType="TFS.ProjectOverview.ReadmeFileRenderer"
                        modules={["ProjectOverview/Scripts/Shared/Components/ReadmeSection/ReadmeFileRenderer"]}
                        key={"readme-file-renderer"}
                    />
                </>
            );
        } else {
            // we show repo dropdown in edit mode always, except while fetching initial data
            const repositoryDropdownDescription = <div id={this._repoDropdownAriaDescribedById} className="hidden" hidden>
                {Resources.RepositoryDropdownDescription}
            </div>;

            const repoDropdown = !this.props.readMode && this.state.dropdownOptions && (
                <>
                    <RepositoryDropdown
                        providerOptions={this.state.dropdownOptions}
                        selectedRepositoryItem={this.getSelectedRepositoryItem()}
                        onSelect={this.onRepoDropdownSelected}
                        className="about-repository-selector repo-dropdown-code-about-region"
                        useDefaultCalloutWidth={true}
                    />
                    {repositoryDropdownDescription}
                </>
            );

            let readmeInfo: JSX.Element | null = null;
            // we show readme info only in read mode and when the content is available
            if (this.props.readMode &&
                this.state.rendererOptions &&
                this.state.dropdownOptions &&
                this.state.dropdownOptions.projectName) {
                const repo = this.state.rendererOptions.repository;
                let iconName = "";
                let repoName = "";
                let readmeUrl = "";
                // in case of tfvc, repository object is not available, so fetch the url using route id.
                if (this.props.repoId && this.props.repoId.startsWith(this._tfvcPrefix)) {
                    iconName = "tfvc-repo";
                    repoName = this.props.repoId;
                    readmeUrl = routeUrl(this.context.pageContext, "ms.vss-code-web.tfvc-files-route", {
                        project: this.state.dropdownOptions.projectName,
                        path: repoName + this._readmeFileName
                    });
                } else if (repo) {
                    iconName = repo.isFork ? "git-fork" : "git";
                    repoName = repo.name;
                    readmeUrl = routeUrl(this.context.pageContext, "ms.vss-code-web.files-route-git", {
                        project: this.state.dropdownOptions.projectName,
                        "vc.GitRepositoryName": repo.name,
                        path: this._readmeFileName,
                        version: getVersionFromBranchName(getFriendlyName(repo.defaultBranch))
                    });
                }

                const readmePath = format("{0} / {1}", repoName, this._readmeString);
                const readmeTitleClassNames = "readme-title font-size-mm font-weight-semibold";
                readmeInfo = (
                    <>
                        <VssIcon iconName={iconName} iconType={VssIconType.bowtie} className={"repo-icon"} />
                        {
                            this.state.showReadmeLink ?
                                <FPSLink
                                    className={css("readme-link link-text", readmeTitleClassNames)}
                                    href={readmeUrl}
                                    telemetrySource={"project_overview_about_region_code"}
                                    removeUnderline={true}
                                >
                                    {readmePath}
                                </FPSLink> :
                                <span className={readmeTitleClassNames}>
                                    {readmePath}
                                </span>
                        }
                    </>
                );
            }

            let readmeSection: JSX.Element | null = null;
            if (this.state.fetchingState === FetchingState.FetchingReadme) {
                readmeSection = this._loadingComponent();
            } else if (this.state.fetchingState === FetchingState.FetchingFailed && this.state.errorOptions && this.props.showError) {
                const { message, title } = this.state.errorOptions;
                readmeSection = (
                    <div className={"about-error-message"} role="alert">
                        <div className="error-title font-weight-semibold">{title}</div>
                        <div className="error-message">{message}</div>
                    </div>
                );
            } else if (this.state.fetchingState === FetchingState.NotFetching && this.state.rendererOptions) {
                const { repository, branchName, filePath, content } = this.state.rendererOptions;
                readmeSection = (
                    <>
                        {!this.props.readMode && <div className="about-preview-title">{Resources.PreviewTitle}</div>}
                        {readmeInfo}
                        <LegacyComponent
                            wrappedType="TFS.ProjectOverview.ReadmeFileRenderer"
                            modules={["ProjectOverview/Scripts/Shared/Components/ReadmeSection/ReadmeFileRenderer"]}
                            key={"readme-file-renderer"}
                            repository={repository}
                            branchName={branchName ? getFriendlyName(branchName) : branchName}
                            filePath={filePath}
                            content={content}
                            onContentRendered={this._onContentRendered}
                            className={"about-readme-file-renderer"}
                            loadingComponent={this._loadingComponent}
                        />
                    </>
                );
            }
            return (
                <div className={css("code-about-region", this.props.readMode ? "readmode" : "editmode")}>
                    {repoDropdown}
                    {readmeSection}
                </div>
            );
        }
    }

    private getSelectedRepositoryItem = (): IRepositoryDropdownItem | undefined => {
        const repositoryItem = this.state.repositoryDropdownItem;

        return (
            repositoryItem ?
                {
                    id: repositoryItem.id,
                    name: repositoryItem.name ? repositoryItem.name : "$/" + repositoryItem.repository?.project.name,
                    isTfvc: !!repositoryItem.isTfvc,
                    isFork: !!repositoryItem.isFork
                }
                :
                {
                    id: getId(Resources.SelectRepositoryLabel),
                    name: Resources.SelectRepositoryLabel
                }
        );
    };

    private onRepoDropdownSelected = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IRepositoryDropdownItem>): void => {
        const repositoryDropdownItem = item.data;
        const currentRepoId = this.state.repositoryDropdownItem && this.state.repositoryDropdownItem.id;

        if (!repositoryDropdownItem ||
            (repositoryDropdownItem.isTfvc && repositoryDropdownItem.name === this.props.repoId) ||
            (!repositoryDropdownItem.isTfvc && repositoryDropdownItem.id === this.props.repoId) ||
            (repositoryDropdownItem.id === currentRepoId)
        ) {
            return;
        }

        this.setState({
            fetchingState: FetchingState.FetchingReadme,
            repositoryDropdownItem
        });

        // Fetch the readme content
        if (repositoryDropdownItem.isTfvc) {
            this.props.onRepositoryChanged(repositoryDropdownItem.name);
            this._fetchTfvcReadme(repositoryDropdownItem.name);
        } else {
            this.props.onRepositoryChanged(repositoryDropdownItem.id);
            this.trackPromise(
                this.gitClient.getItem(
                    repositoryDropdownItem.id,
                    this._readmeFileName,
                    undefined, /* project */
                    undefined, /* scopePath */
                    undefined, /* recursionLevel */
                    undefined, /* includeContentMetadata */
                    undefined, /* latestProcessedChange */
                    undefined, /* download */
                    undefined, /* versionDescriptor */
                    true
                )
            ).promise.then((gitItem: GitItem) => {
                this.setState({
                    fetchingState: FetchingState.NotFetching,
                    rendererOptions: {
                        content: gitItem.content,
                        repository: repositoryDropdownItem.repository,
                        filePath: this._readmeFileName,
                        branchName: repositoryDropdownItem.repository ? repositoryDropdownItem.repository.defaultBranch : "master"
                    }
                });
            }, this._onFetchingReadmeFailed);
        }

    };

    private _onContentRendered = (): void => {
        if (!this._ttiMarked && this.props.readMode) {
            this.props.markTimeToInteractive(this._codeSource);
            this._ttiMarked = true;
        }
    };

    private _loadingComponent(): JSX.Element {
        return <Spinner className="about-loading-spinner" />;
    }

    private _fetchInitialData(props: ICodeAboutRegionProps, context: IVssComponentContext): void {
        this._addSplitTiming("Code.FetchInitialData.Start");

        this.trackPromise(
            context.pageContext
                .getService<IVssContributionService>("IVssContributionService")
                .getDataAsync<{ supportsTfvc: boolean }>(CodeAboutRegion.PROVIDER_ID, { scope: "supportsTfvc" })
        ).promise.then(
            data => {
                const supportsTfvc = data && data.supportsTfvc;
                const { projectId, projectName } = this._getProjectInfo(context.pageContext);
                if (props.repoId) {
                    if (props.repoId.startsWith(this._tfvcPrefix)) {
                        // incase of tfvc, there is no concept of repoId, so we set it to "tfvc"
                        // and repoName is "$/"+ProjectName, which comes as props.repoId in this case.
                        // update the dropdown.
                        const dropdownOptions = this.getDefaultDropdownOptions(projectId, projectName, supportsTfvc);
                        const repositoryDropdownItem: IRepositoryDropdownItem = {
                            name: props.repoId,
                            isTfvc: true,
                            isFork: false,
                            id: "tfvc"
                        };
                        this.setState({
                            fetchingState: FetchingState.FetchingReadme,
                            dropdownOptions,
                            repositoryDropdownItem
                        });
                        // fetch the tfvc readme
                        this._fetchTfvcReadme(props.repoId, true);
                    } else {
                        // fetch the git repository information and readme parallelly
                        // incase of read-mode, use data provider to fetch repository information, to support showing readme in anonymous scenario
                        const repositoryPromise = this.props.readMode
                            ? this.trackPromise(
                                context.pageContext
                                    .getService<IVssContributionService>("IVssContributionService")
                                    .getDataAsync<CodeRepositoryDataProviderData>(CodeAboutRegion.PROVIDER_ID, { scope: "gitRepositoryInfo" }, true)
                                    .then(data => {
                                        return data && data.codeRepositoryData.repository;
                                    })
                            ).promise
                            : this.trackPromise(this.gitClient.getRepository(props.repoId)).promise;
                        const readmePromise = this.gitClient.getItem(
                            props.repoId,
                            this._readmeFileName,
                            projectId,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            true
                        );
                        Promise_Util.allSettled<GitItem | GitRepository | void>([repositoryPromise, readmePromise]).then(
                            (results: Promise_Util.IPromiseResult[]) => {
                                const rejectedString = "rejected";
                                if (results[0].state === rejectedString) {
                                    this._addSplitTiming(this._abortSplitTimeName);
                                    // if repository information failed to load
                                    const dropdownOptions = this.getDefaultDropdownOptions(projectId, projectName, supportsTfvc);
                                    const error = (results[0] as Promise_Util.IRejectedPromiseResult).reason;
                                    this._onFetchingInitialDataFailed(dropdownOptions, error);
                                } else {
                                    const repository = (results[0] as Promise_Util.IResolvedPromiseResult<GitRepository>).value;
                                    if (!repository) {
                                        this._addSplitTiming(this._abortSplitTimeName);
                                        // if repository not found
                                        const dropdownOptions = this.getDefaultDropdownOptions(projectId, projectName, supportsTfvc);
                                        this._onFetchingInitialDataFailed(dropdownOptions);
                                    } else {
                                        const dropdownOptions = this.getDefaultDropdownOptions(projectId, projectName, supportsTfvc);
                                        if (results[1].state === rejectedString) {
                                            this._addSplitTiming(this._abortSplitTimeName);
                                            // if readme content failed to load
                                            const error = (results[1] as Promise_Util.IRejectedPromiseResult).reason;
                                            this._onFetchingInitialDataFailed(dropdownOptions, error);
                                        } else {
                                            this._addSplitTiming(this._endSplitTimeName);
                                            const gitItem: GitItem = (results[1] as Promise_Util.IResolvedPromiseResult<GitItem>).value;
                                            const rendererOptions: IRendererOptions = {
                                                content: gitItem.content,
                                                repository: repository,
                                                filePath: this._readmeFileName,
                                                branchName: repository.defaultBranch
                                            };
                                            const repositoryDropdownItem: IRepositoryDropdownItem = {
                                                name: repository.name,
                                                isTfvc: false,
                                                isFork: repository.isFork,
                                                id: repository.id,
                                                repository
                                            };
                                            this.setState({
                                                fetchingState: FetchingState.NotFetching,
                                                dropdownOptions,
                                                rendererOptions,
                                                repositoryDropdownItem
                                            });
                                        }
                                    }
                                }
                            }
                        );
                    }
                } else {
                    this._addSplitTiming(this._abortSplitTimeName);
                    // if a repository is not configured
                    const dropdownOptions = this.getDefaultDropdownOptions(projectId, projectName, supportsTfvc);
                    this.setState({
                        fetchingState: FetchingState.FetchingFailed,
                        dropdownOptions,
                        errorOptions: undefined
                    });
                }
            },
            () => {
                this._addSplitTiming(this._abortSplitTimeName);
            }
        );
    }

    private _getProjectInfo(pageContext: IVssPageContext): { projectId?: string, projectName?: string } {
        const tfsPageData = pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project;
            return {
                projectId: project.id,
                projectName: project.name
            };
        }

        return {};
    }

    private getDefaultDropdownOptions(projectId?: string, projectName?: string, supportsTfvc?: boolean): IRepositoryDropdownOptions {
        return {
            projectId: projectId,
            projectName: projectName,
            supportsTfvc: supportsTfvc,
        };
    }

    private _fetchTfvcReadme(basePath: string, isInitial?: boolean): void {
        const filePath = basePath + this._readmeFileName;
        const tfvcRestClient = this.context.pageContext.getRestClient<ITfvcRestClient>("ITfvcRestClient");

        this.trackPromise(
            tfvcRestClient.getItem(filePath, undefined, undefined, undefined, undefined, undefined, undefined, true)
        ).promise.then(
            (tfvcItem: TfvcItem) => {
                if (isInitial) {
                    this._addSplitTiming(this._endSplitTimeName);
                }

                const rendererOptions: IRendererOptions = {
                    content: tfvcItem.content,
                    repository: undefined,
                    filePath: filePath,
                    branchName: undefined
                };
                this.setState({
                    fetchingState: FetchingState.NotFetching,
                    rendererOptions: rendererOptions
                });
            },
            error => {
                this._onFetchingReadmeFailed(error, isInitial);
            }
        );
    }

    private _onFetchingReadmeFailed = (error: Promise_Util.ICancelReason | errorStatus, isInitial?: boolean): void => {
        if ((error as Promise_Util.ICancelReason).isCanceled) {
            return;
        }

        if (isInitial) {
            this._addSplitTiming(this._abortSplitTimeName);
        }

        this.setState({
            fetchingState: FetchingState.FetchingFailed,
            rendererOptions: undefined,
            errorOptions: this._getErrorOptions(error as errorStatus)
        });
    }

    private _onFetchingInitialDataFailed = (
        dropdownOptions?: IRepositoryDropdownOptions,
        error?: Promise_Util.ICancelReason | errorStatus
    ): void => {
        if (error && (error as Promise_Util.ICancelReason).isCanceled) {
            return;
        }
        this.setState({
            fetchingState: FetchingState.FetchingFailed,
            dropdownOptions,
            rendererOptions: undefined,
            errorOptions: error ? this._getErrorOptions(error as errorStatus) : {
                title: Resources.ReadmeNotFoundTitle,
                message: Resources.ReadmeNotFoundMessage
            }
        });
    }

    private _getErrorOptions(error: errorStatus): IErrorOptions | undefined {
        // show no readme found if server returns bad request. Assuming it means itemModel not found
        // status code: 400 is thrown if the repository is empty
        // otherwise if the readme is not found then 404 is thrown
        return error && (error.status === 400 || error.status === 404)
            ? {
                title: Resources.ReadmeNotFoundTitle,
                message: Resources.ReadmeNotFoundMessage
            }
            : undefined;
    }

    private _addSplitTiming(name: string): void {
        if (this._ttiMarked) {
            return;
        }
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.addSplitTiming(name);
    }

    private get gitClient(): IGitRestClient {
        if (!this._gitClient) {
            this._gitClient = this.context.pageContext.getRestClient<IGitRestClient>("IGitRestClient");
        }
        return this._gitClient;
    }
}
